/**
 * Right-hand anchored-gutter comments (#2244).
 *
 * Every open comment renders simultaneously as a card in a dedicated
 * column to the right of the editor. Each card wants to sit level with
 * its anchor word; when two anchors are close enough that the cards
 * would overlap, the later one slides down (see `stack-cards.ts`) and a
 * connector line is drawn from the anchor back to the card. The draft
 * comment is one more card at the user's selection — same layout path,
 * so the compose textarea takes focus (#2244 reopen) without any
 * modal in the comment path.
 *
 * The gutter is a sibling of the Virtuoso scroller inside the editor's
 * overflow wrapper. It uses `useAnchorTracker` to re-measure anchors
 * on scroll + resize + character mutations, so cards stay level with
 * their paragraphs when the user types, resizes the window, or
 * virtualises rows in and out.
 */

// NOTE: the gutter cards stay white regardless of theme — see the
// matching comment in `gutter-slot.tsx` where those hardcoded hex
// colors actually live now. The eslint-disable moved with them when
// `GutterSlot` was extracted.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Comment } from '@utils/types/comments/comments.ts';
import { CommentCard } from './comment-card.tsx';
import { useCommentsContext } from './comments-context.tsx';
import { useCommentFilter } from './comments-filter-context.tsx';
import { filterFlagsFor } from './comments-selectors.ts';
import { GutterSlot } from './gutter-slot.tsx';
import {
  useAnchorTracker,
  type AnchorEntryInput
} from './use-anchor-tracker.ts';
import { stackCards, type CardLayoutInput } from './stack-cards.ts';

interface Props {
  /** The editor's scroll container (Virtuoso scroller). Used to resolve
   *  word anchors + listen for scroll/resize/mutation. */
  scrollRootRef: React.RefObject<HTMLElement | null>;
  /** Fixed gutter column width. Must match the `padding-right` the
   *  editor rows reserve so the cards don't overlap the text. */
  columnWidth?: number;
  /** Minimum pixel distance between stacked cards (passed through to
   *  `stackCards`). */
  cardGap?: number;
}

const DRAFT_KEY = 'draft';

export function CommentsGutter({
  scrollRootRef,
  columnWidth = 320,
  cardGap = 12
}: Props) {
  const ctx = useCommentsContext();
  const { t } = useTranslation();

  // Respect the gutter's Open / Resolved / All filter. Previously the
  // gutter rendered every comment regardless of filter so the user
  // could un-resolve from here — but that violated #2246's AC "resolve
  // moves a comment from Open to Resolved and updates both counts": a
  // card marked RESOLVED was staying in the translation gutter with
  // the filter set to Open (reopen comment from 2h ago). Unresolving
  // still works — it just happens from the Resolved / All view.
  const { filter } = useCommentFilter();
  const { showOpen, showResolved } = filterFlagsFor(filter);
  const visibleComments = useMemo(() => {
    if (!ctx) return [] as Comment[];
    return ctx.allComments.filter(c => {
      const resolved = !!c.resolved;
      return (resolved && showResolved) || (!resolved && showOpen);
    });
  }, [ctx, showOpen, showResolved]);

  // One entry per visible card: every filter-matching comment + the
  // draft (if any). The draft always renders so a compose-in-progress
  // doesn't vanish when the user flips to the Resolved filter.
  const entries = useMemo<AnchorEntryInput[]>(() => {
    if (!ctx) return [];
    const list: AnchorEntryInput[] = visibleComments.map(c => ({
      key: `c:${c.id}`,
      indexFrom: c.index_from ?? undefined,
      indexTo: c.index_to ?? undefined
    }));
    if (ctx.draft) {
      list.push({
        key: DRAFT_KEY,
        indexFrom: ctx.draft.indexFrom,
        indexTo: ctx.draft.indexTo
      });
    }
    return list;
  }, [ctx, visibleComments]);

  const anchorRects = useAnchorTracker(entries, scrollRootRef);

  // Measured card heights. Starts at 0 and gets filled as cards mount
  // and report via ResizeObserver (see `CommentCard.onHeightChange`).
  const [heights, setHeights] = useState<Record<string, number>>({});
  const reportHeight = useCallback((key: string, h: number) => {
    setHeights(prev => (prev[key] === h ? prev : { ...prev, [key]: h }));
  }, []);

  // Leaf-marker click (amber/emerald icon in the body) fires
  // `transcript-comments:focus` with `{ commentId }`. The old popover
  // gutter opened a card at that icon; the new gutter already shows
  // every comment, so we just briefly highlight the matching card so
  // the user can tell which gutter row the icon they just clicked
  // belongs to. Clears itself after 1.5s.
  const [highlightedKey, setHighlightedKey] = useState<string | null>(null);
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ commentId: number | string }>).detail;
      if (!detail?.commentId) return;
      setHighlightedKey(`c:${detail.commentId}`);
    };
    window.addEventListener('transcript-comments:focus', handler);
    return () =>
      window.removeEventListener('transcript-comments:focus', handler);
  }, []);
  useEffect(() => {
    if (!highlightedKey) return;
    const timer = window.setTimeout(() => setHighlightedKey(null), 1500);
    return () => window.clearTimeout(timer);
  }, [highlightedKey]);

  const layoutInputs: CardLayoutInput[] = useMemo(() => {
    const inputs: CardLayoutInput[] = [];
    for (const entry of entries) {
      const rect = anchorRects[entry.key];
      // Anchor not resolved (paragraph virtualised out of DOM) → skip
      // this round. The card will reappear when the user scrolls to it.
      if (!rect) continue;
      inputs.push({
        key: entry.key,
        anchorTop: rect.top,
        height: heights[entry.key] ?? 0
      });
    }
    return inputs;
  }, [entries, anchorRects, heights]);

  const layout = useMemo(
    () => stackCards(layoutInputs, cardGap),
    [layoutInputs, cardGap]
  );

  if (!ctx) return null;
  if (entries.length === 0) return null;

  // Only visible comments can be rendered — anything else is filtered
  // out by the Open / Resolved toggle above.
  const commentById = new Map<string, Comment>(
    visibleComments.map(c => [`c:${c.id}`, c])
  );

  return (
    <div
      aria-label={t('comments')}
      style={{
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        width: columnWidth,
        pointerEvents: 'none',
        overflow: 'hidden',
        zIndex: 20
      }}
    >
      {layout.map(entry => {
        const isDraft = entry.key === DRAFT_KEY;
        const comment = commentById.get(entry.key);
        if (!isDraft && !comment) return null;
        return (
          <GutterSlot
            key={entry.key}
            top={entry.top}
            anchorTop={entry.anchorTop}
            pushed={entry.pushed}
          >
            {isDraft ? (
              <CommentCard
                id={DRAFT_KEY}
                authorName={t('you')}
                createdAt={undefined}
                initialText=""
                initialMode="edit"
                onHeightChange={h => reportHeight(DRAFT_KEY, h)}
                onSave={async next => {
                  if (!ctx.draft) return;
                  await ctx.createOne({
                    text: next,
                    indexFrom: ctx.draft.indexFrom,
                    indexTo: ctx.draft.indexTo
                  });
                  ctx.cancelDraft();
                }}
                onCancel={ctx.cancelDraft}
              />
            ) : (
              comment && (
                <CommentCard
                  id={`c:${comment.id}`}
                  authorName={comment.created_by_name}
                  createdAt={comment.created}
                  initialText={comment.text ?? ''}
                  resolved={!!comment.resolved}
                  readOnly={ctx.readOnly}
                  ownershipLabel={ctx.ownershipLabel}
                  highlighted={highlightedKey === `c:${comment.id}`}
                  onHeightChange={h => reportHeight(`c:${comment.id}`, h)}
                  onSave={async next => {
                    await ctx.updateOne(
                      comment.id,
                      next,
                      comment.index_from ?? '',
                      comment.index_to ?? ''
                    );
                  }}
                  onDelete={
                    ctx.readOnly
                      ? undefined
                      : async () => {
                          await ctx.deleteOne(comment.id);
                        }
                  }
                  onToggleResolved={
                    ctx.readOnly ? undefined : () => ctx.toggleResolved(comment)
                  }
                />
              )
            )}
          </GutterSlot>
        );
      })}
    </div>
  );
}

// `GutterSlot` moved to its own file so this one stays under the
// 300-LOC budget — see `./gutter-slot.tsx`.
